import cn from 'classnames';
import { capitalize, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useIntl } from 'react-intl';
import { ButtonUI, EmptyState, LoadingSkeleton, Switch, TaptapList, useQueryFetch } from 'sonata-react-components';
import { Formatters } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';
import { formatMetrics } from 'sonata-static-storage/lib/helpers/metrics.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useModals, useNavigate } from '@/router';
import { getQuickReport } from '@/services/quickReport.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { QuickReportChart } from './QuickReportChat/QuickReportChart';
import {
  buildChartValues,
  buildTooltipPoints,
  CHART_DAYS,
  GOAL_LABEL,
  getChartDays,
  getGoalIcon,
  getGoalMetric
} from './QuickReportChat/quickReportChart.helper';

import type {
  CampaignPlanQuickReport,
  InsertionOrderQuickReport,
  LineItemQuickReport,
  MultiFlightEntityType,
  MultiFlightQuickReport,
  MultiFlightResumeStatsTotals
} from 'sonata-static-storage/lib/common/common.types';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';
import type { EntityWindow } from './QuickReportChat/quickReportChart.helper';

import './quickReport.scss';

type Summary = {
  [key: string]: {
    amount: string;
    percentage?: string;
    subtitle: string;
  }[];
};

type StatsTableRow = {
  id: string;
  kpi: string;
  today?: string | number;
  total: string | number;
};

type Props = {
  entity: CampaignPlan | InsertionOrder | any;
  entityType: MultiFlightEntityType;
  id: string;
  metrics?: string[];
};

const QuickReport = ({ entity, entityType, id, metrics = ['total_spend'] }: Props) => {
  const entityId = entity._id?.toString() as string;

  const { formatMessage } = useIntl();
  const modals = useModals();
  const navigate = useNavigate();
  const userEntity = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const currencyInfo = useGlobalStore(getBaseCurrency);

  const { country, id: userId } = userEntity.toJSON();

  const filterQuickReportProgressStats = (
    progress: MultiFlightQuickReport['progress']
  ): MultiFlightQuickReport['progress'] => {
    const filterQuickReportProgressStatsEntry = (statsEntry: Record<string, any>) => {
      return Object.keys(statsEntry)
        .filter(statsKey => {
          if (['gross_margin', 'total_gross_margin'].includes(statsKey)) {
            return checkUserResourcePermissions('campaignListGm', userEntity.entity, userRoles);
          }
          const statsKeyFormatted = statsKey.split('_').reduce((acc, substring) => {
            return `${acc}${substring.substring(0, 1).toUpperCase()}${substring.substring(1)}`;
          }, '');
          return checkUserResourcePermissions(`reporting${statsKeyFormatted}`, userEntity.entity, userRoles);
        })
        .reduce(
          (acc, statsKey) => ({
            ...acc,
            [statsKey]: statsEntry[statsKey]
          }),
          {}
        );
    };

    return {
      ...(progress.today && {
        today: filterQuickReportProgressStatsEntry(progress.today)
      }),
      total: filterQuickReportProgressStatsEntry(progress.total) as MultiFlightResumeStatsTotals
    };
  };

  const { data, isLoading } = useQueryFetch<CampaignPlanQuickReport | InsertionOrderQuickReport | LineItemQuickReport>(
    ['quickReport', entityType, metrics, entityId],
    async ({ signal }) => {
      const quickReportData = await getQuickReport<
        CampaignPlanQuickReport | InsertionOrderQuickReport | LineItemQuickReport
      >(entityType, userId as string, entityId, metrics, signal);
      return {
        ...quickReportData,
        progress: filterQuickReportProgressStats(quickReportData.progress)
      };
    },
    // No cache: each opening fetches again; closing unmounts the query and aborts the request
    { cacheTime: 0 }
  );

  const messageformattedEntity = {
    campaign: 'Line Item',
    campaignPlan: 'Campaign',
    insertionOrder: 'Insertion Order',
    lineItem: 'Line Item'
  }[entityType];

  const notAvailableMessage = formatMessage({
    defaultMessage: 'Not available',
    id: 'common.quickReport.info.notAvailable'
  });

  const infoDate = (() => {
    const rangeBeginDate = data?.beginDate;
    const rangeEndDate = data?.endDate;
    if (!rangeBeginDate || !rangeEndDate) {
      return notAvailableMessage;
    }
    const rangeBeginMoment = moment(rangeBeginDate);
    const rangeEndMoment = moment(rangeEndDate);
    if (!rangeBeginMoment.isValid() || !rangeEndMoment.isValid()) {
      return notAvailableMessage;
    }
    const daysCount = rangeEndMoment.diff(rangeBeginMoment, 'days') + 1;
    return `${rangeBeginMoment.format('MMM D')} – ${rangeEndMoment.format('MMM D, YYYY')} · ${daysCount} ${formatMessage(
      {
        defaultMessage: 'days',
        id: 'common.quickReport.info.days'
      }
    )}`;
  })();

  const currentDate = moment.utc().endOf('day');
  const endDate = data?.endDate && moment.utc(data?.endDate).endOf('day');
  const entityServiceTimeHasExpired = endDate ? currentDate.diff(endDate, 'days') > 0 : true;

  const tableData: StatsTableRow[] = (() => {
    const kpis = Object.keys(data?.progress?.total ?? {});
    const formattedTodayStats = formatMetrics(data?.progress?.today ?? {}, currencyInfo as any, country);
    const formattedTotalStats = formatMetrics(data?.progress?.total ?? {}, currencyInfo as any, country);

    return kpis.map(kpi => ({
      id: kpi,
      kpi: formatMessage({
        defaultMessage: kpi
          .split('_')
          .map(word => capitalize(word))
          .join(' '),
        id: `common.quickReport.stats.kpis.${kpi}`
      }),
      total: formattedTotalStats[kpi] ?? 0,
      ...(!entityServiceTimeHasExpired && { today: formattedTodayStats[kpi] ?? 0 })
    }));
  })();

  const goals = data?.resumeStats.goals || [];

  const getGoalLabel = (goalType: string) => GOAL_LABEL[goalType] ?? capitalize(goalType.replace(/_/g, ' '));

  const formatGoalValue = (goalType: string, value: number) =>
    goalType === 'budget' ? Formatters.currency(value, currencyInfo as any) : Formatters.number(value, { fixed: 0 });

  const getGoalSubtitles = (goalType: string) => {
    const label = getGoalLabel(goalType);
    return goalType === 'budget'
      ? { current: 'Total Spent', target: 'Budget Goal', today: 'Spent Today' }
      : { current: label, target: `${label} Goal`, today: `${label} Today` };
  };

  const summaryData =
    isLoading || !data
      ? ({} as Summary)
      : (goals.reduce((acc, actualItem) => {
          const subtitles = getGoalSubtitles(actualItem.type);

          return {
            ...acc,
            [actualItem.type]: [
              {
                amount: formatGoalValue(actualItem.type, actualItem.target ?? 0),
                subtitle: formatMessage({
                  defaultMessage: subtitles.target,
                  id: `common.quickReport.summary.${actualItem.type}.target`
                })
              },
              {
                amount: formatGoalValue(actualItem.type, actualItem.current ?? 0),
                percentage: Formatters.percentage(actualItem.progressPct ?? 0, {
                  base: 100,
                  fixedDecimals: 2
                }),
                subtitle: formatMessage({
                  defaultMessage: subtitles.current,
                  id: `common.quickReport.summary.${actualItem.type}.current`
                })
              },
              {
                amount: formatGoalValue(actualItem.type, data.progress.today?.[getGoalMetric(actualItem.type)] ?? 0),
                subtitle: formatMessage({
                  defaultMessage: subtitles.today,
                  id: `common.quickReport.summary.${actualItem.type}.today`
                })
              }
            ].filter((_, index) => index !== 2 || !entityServiceTimeHasExpired)
          };
        }, {}) as Summary);

  const isChartDataEmpty = isEmpty(data?.stats);
  const chartLastDay =
    data?.endDate && entityServiceTimeHasExpired
      ? moment.utc(data.endDate).startOf('day')
      : moment.utc().startOf('day');
  const chartDays = getChartDays(chartLastDay);
  const chartCategories = chartDays.map(day => day.format('D MMM'));

  // "Day X of N" window: IO flights, LI schedule, campaign plan report dates
  const entityWindow: EntityWindow | undefined = (() => {
    if (entityType === 'insertionOrder' && !entity.flights?.length) {
      return undefined;
    }
    const [beginDate, endDate] =
      entityType === 'insertionOrder'
        ? [
            moment.min((entity.flights ?? []).map((f: { beginDate: string }) => moment.utc(f.beginDate))),
            moment.max((entity.flights ?? []).map((f: { endDate: string }) => moment.utc(f.endDate)))
          ]
        : entityType === 'lineItem'
          ? [moment.utc(entity.beginDate), moment.utc(entity.endDate)]
          : [moment.utc(data?.beginDate), moment.utc(data?.endDate)];
    return beginDate?.isValid() && endDate?.isValid()
      ? { begin: beginDate.startOf('day'), end: endDate.startOf('day') }
      : undefined;
  })();

  // One chart per overall goal: budget plots the spend, any other goal its own metric.
  const chartsData = goals.map(g => {
    const values = buildChartValues(chartDays, data?.stats, getGoalMetric(g.type));
    return {
      categories: chartCategories,
      data: isChartDataEmpty ? Array.from({ length: CHART_DAYS }).map(() => 100) : values,
      formatValue: (value: number) => formatGoalValue(g.type, value),
      goalLabel: getGoalLabel(g.type),
      goalType: g.type,
      height: goals.length === 1 ? 200 : 40,
      icon: getGoalIcon(g.type),
      isEmpty: isChartDataEmpty,
      key: g.type,
      legend: formatMessage({
        defaultMessage: 'Last 10 days',
        id: `common.quickReport.summary.${g.type}.legend`
      }),
      seriesName:
        g.type === 'budget'
          ? formatMessage({ defaultMessage: 'Spend', id: 'common.quickReport.chart.spend' })
          : formatMessage({
              defaultMessage: getGoalSubtitles(g.type).current,
              id: `common.quickReport.summary.${g.type}.current`
            }),
      summary: summaryData[g.type] || [],
      target: g.target ?? 0,
      title: formatMessage({
        defaultMessage: 'Summary',
        id: `common.quickReport.summary.${g.type}.title`
      }),
      tooltipPoints: buildTooltipPoints(chartDays, values, g.current ?? 0, g.target ?? 0, entityWindow)
    };
  });

  const handleOnViewDetailClick = () => {
    if (entityType === 'campaignPlan') {
      modals.open('/campaigns/new/campaignPlanSettingsModal', {
        at: {
          pathname: '/campaigns',
          search: location.search
        },
        state: {
          campaignPlan: entity,
          modalId: 'campaignPlanSettings-modal'
        }
      });
    } else if (entityType === 'insertionOrder') {
      navigate('/ioSettings/edit/:id', {
        params: {
          id: entityId
        },
        state: {
          insertionOrder: entity
        }
      });
    } else if (entityType === 'lineItem') {
      navigate('/liSettings/edit/:id', {
        params: {
          id: entityId
        },
        state: {
          lineItem: entity
        }
      });
    }
  };

  return (
    <div>
      <div className="quickReportTop">
        <div className="quickReportTop__infoAndStatsSection">
          <div className="quickReportTop__infoAndStatsSectionInfo">
            <div className="quickReportCard" id={`${id}_${entityId}_quickReportTop_info`}>
              <div className="quickReportCard__body quickReportCard__body--noHeader">
                {isLoading ? (
                  <LoadingSkeleton height={20} />
                ) : (
                  <div className="quickReportTop__infoAndStatsSectionInfoDateRow">
                    <i className="quickReportTop__infoAndStatsSectionInfoDateIcon fal fa-calendar" />
                    <span className="quickReportTop__infoAndStatsSectionInfoDateLabel">
                      {formatMessage({
                        defaultMessage: `${messageformattedEntity} dates`,
                        id: `common.quickReport.info.date.${entityType}`
                      })}
                    </span>
                    <span className="quickReportTop__infoAndStatsSectionInfoDate">{infoDate}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="quickReportTop__infoAndStatsSectionStats">
            <div className="quickReportCard" id={`${id}_${entityId}_quickReportTop_stats`}>
              <div className="quickReportCard__header">
                <i className="quickReportCard__headerIcon fal fa-chart-bar" />
                <span className="quickReportCard__headerTitle">
                  {formatMessage({
                    defaultMessage: 'Stats',
                    id: 'common.quickReport.stats.title'
                  })}
                </span>
              </div>
              <div className="quickReportCard__body">
                <Switch>
                  <Switch.Case condition={isLoading}>
                    <div className="quickReportStats__loading">
                      <LoadingSkeleton containerClassName="quickReportStats__loadingSkeleton" height="100%" />
                    </div>
                  </Switch.Case>
                  <Switch.Case condition={isEmpty(tableData)}>
                    <EmptyState
                      icon="fa-solid fa-empty-set"
                      id={`${id}_${entityId}_quickReportTop_statsEmptyState`}
                      padding={50}
                      show
                      title={formatMessage({
                        defaultMessage: 'There are currently no stats to display',
                        id: 'common.quickReport.stats.noData'
                      })}
                    />
                  </Switch.Case>
                  <Switch.Default>
                    <div
                      className={cn('quickReportStats', {
                        'quickReportStats--noToday': entityServiceTimeHasExpired
                      })}
                    >
                      <TaptapList<StatsTableRow>
                        className="quickReportStats__list"
                        data={tableData}
                        id={`${id}_${entityId}_quickReportTop_stats_summary`}
                      >
                        <TaptapList.ColumnsBar>
                          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
                          <TaptapList.ColumnsBar.Column
                            description={formatMessage({
                              defaultMessage: 'KPI',
                              id: 'common.quickReport.stats.kpi'
                            })}
                            id="kpi"
                          />
                          {!entityServiceTimeHasExpired && (
                            // biome-ignore lint: column IDs are semantic keys, not DOM ids
                            <TaptapList.ColumnsBar.Column
                              className="quickReportStats__todayColumn"
                              description={formatMessage({
                                defaultMessage: 'Today',
                                id: 'common.quickReport.stats.today'
                              })}
                              id="today"
                            />
                          )}
                          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
                          <TaptapList.ColumnsBar.Column
                            className="quickReportStats__totalColumn"
                            description={formatMessage({
                              defaultMessage: 'Total',
                              id: 'common.quickReport.stats.total'
                            })}
                            id="total"
                          />
                        </TaptapList.ColumnsBar>
                        <TaptapList.RowsList<StatsTableRow>>
                          {rows =>
                            rows.map(row => (
                              <TaptapList.RowsList.Row id={row.id} key={row.id}>
                                <span className="quickReportStats__kpiCell">{row.kpi}</span>
                                {!entityServiceTimeHasExpired && (
                                  <span className="quickReportStats__todayCell">{row.today}</span>
                                )}
                                <span className="quickReportStats__totalCell">{row.total}</span>
                              </TaptapList.RowsList.Row>
                            ))
                          }
                        </TaptapList.RowsList>
                      </TaptapList>
                    </div>
                  </Switch.Default>
                </Switch>
              </div>
            </div>
          </div>
        </div>
        <div className="quickReportTop__charts">
          {isLoading ? (
            <LoadingSkeleton height={418} />
          ) : (
            chartsData.map(chartData => <QuickReportChart chart={chartData} id={id} key={chartData.key} />)
          )}
        </div>
      </div>
      <div className="quickReportBottom">
        <ButtonUI
          icon="fal fa-arrow-right"
          iconPosition="right"
          onClick={handleOnViewDetailClick}
          shape="squareRounded"
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'View detail',
            id: 'common.quickReport.button'
          })}
          type="secondary"
        />
      </div>
    </div>
  );
};

export default QuickReport;
